import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import type { Locale } from "date-fns";
import { de, enUS, fr, it } from "date-fns/locale";

export type Language = "de" | "en" | "fr" | "it";

type TranslationParams = Record<string, string | number>;

type I18nContextValue = {
  language: Language;
  setLanguage: (language: Language) => void;
  t: (key: string, params?: TranslationParams) => string;
  locale: string;
  dateLocale: Locale;
};

const STORAGE_KEY = "ui_language";

const translations: Record<Language, Record<string, string>> = {
  de: {
    "language.label": "Sprache",
    "language.de": "Deutsch",
    "language.en": "English",
    "language.fr": "Franzosisch",
    "language.it": "Italiano",
    "common.settings": "Einstellungen",
    "common.profile": "Profil bearbeiten",
    "common.cancel": "Abbrechen",
    "common.save": "Speichern",
    "common.saving": "Speichert...",
    "common.loading": "Lade...",
    "common.all": "Alle",
    "common.none": "Keine",
    "common.search": "Suchen",
    "common.delete": "Loschen",
    "common.edit": "Bearbeiten",
    "common.logout": "Abmelden",
    "common.you": "Du",
    "common.from": "Von",
    "common.to": "Bis",
    "common.interval": "Intervall",
    "common.previous": "Zuruck",
    "common.next": "Weiter",
    "nav.assistant": "Wissensassistent",
    "nav.documents": "Dokumente",
    "nav.users": "Benutzer",
    "nav.settings": "Einstellungen",
    "nav.responses": "Generierte Antworten",
    "nav.analytics": "Analytics",
    "role.admin": "Admin",
    "role.superadmin": "Superadmin",
    "role.beta": "Beta",
    "settingsDropdown.logoName": "Logo Name andern",
    "settingsDropdown.newLogoName": "Neuer Logo Name",
    "settingsDropdown.color": "Farbe andern",
    "settingsDropdown.chooseColor": "Farbe wahlen",
    "settingsDropdown.switchToAdmin": "Zu Admin wechseln",
    "settingsDropdown.language": "Sprache",
    "settingsDropdown.memberAssistant": "Wissensassistent",
    "adminSidebar.title": "{appName} {role}",
    "memberSidebar.pageTitle": "{appName} Wissensassistent",
    "memberSidebar.newChat": "Neuen Chat starten",
    "memberSidebar.assistant": "Assistent",
    "memberSidebar.noThreads": "Noch keine Unterhaltungen",
    "memberSidebar.loadMore": "Weitere laden...",
    "memberSidebar.threadActions": "Thread-Aktionen",
    "memberSidebar.today": "HEUTE",
    "memberSidebar.yesterday": "GESTERN",
    "memberSidebar.previous7": "VORHERIGE 7 TAGE",
    "memberSidebar.previous30": "VORHERIGE 30 TAGE",
    "memberSidebar.olderThan30": "MEHR ALS 30 TAGE",
    "memberSidebar.archiveTitle": "Thread archivieren",
    "memberSidebar.archiveConfirm": "Mochtest du diesen Thread wirklich archivieren? Der Thread wird aus deiner Ansicht entfernt, bleibt aber fur Administratoren sichtbar.",
    "memberSidebar.archive": "Archivieren",
    "assistant.errorGeneric": "Entschuldigung, es ist ein Fehler aufgetreten. Bitte versuche es spater erneut.",
    "assistant.feedbackTooltip": "Dein Feedback wird vom Team gepruft. Bewertungen helfen uns, falsche Antworten zu erkennen und die zugrunde liegenden Dokumente zu verbessern.",
    "assistant.copySuccess": "In Zwischenablage kopiert",
    "assistant.copyTooltip": "Antwort ohne Quellenabschnitt in die Zwischenablage kopieren.",
    "assistant.commentAdd": "Kommentar fur das Team hinzufugen, z. B. was falsch ist oder fehlt.",
    "assistant.commentEdit": "Kommentar fur das Team bearbeiten, z. B. was falsch ist oder fehlt.",
    "assistant.regenerate": "Neue Antwort zur gleichen Frage generieren.",
    "assistant.yourComment": "Dein Kommentar:",
    "assistant.deleteComment": "Kommentar loschen",
    "assistant.webPlaceholder": "Durchsuche das Web... (Enter zum Senden)",
    "assistant.askPlaceholder": "Frag etwas... (Enter zum Senden)",
    "assistant.switchToWeb": "Zur Websuche wechseln",
    "assistant.switchToDocs": "Zur Dokumentensuche wechseln",
    "assistant.stopRecording": "Aufnahme stoppen",
    "assistant.transcribing": "Transkription lauft...",
    "assistant.micUnsupported": "Sprachaufnahme wird in diesem Browser nicht unterstutzt",
    "assistant.startRecording": "Sprachaufnahme starten",
    "assistant.askButton": "Fragen",
    "assistant.transcriptionFailed": "Transkription fehlgeschlagen",
    "assistant.transcriptionNoText": "Die Spracherkennung konnte keinen Text erkennen.",
    "assistant.transcriptionError": "Es gab einen Fehler bei der Spracherkennung.",
    "assistant.transcriptionTooShort": "Die Aufnahme war zu kurz oder leer. Bitte versuche es erneut.",
    "assistant.transcriptionNetwork": "Netzwerkfehler bei der Spracherkennung. Bitte uberprufe deine Internetverbindung.",
    "assistant.commentSaved": "Kommentar erfolgreich gespeichert",
    "assistant.commentSaveFailed": "Kommentar konnte nicht gespeichert werden",
    "assistant.commentDeleted": "Kommentar erfolgreich geloscht",
    "assistant.commentDeleteFailed": "Kommentar konnte nicht geloscht werden",
    "assistant.microphonePermission": "Bitte erlaube den Zugriff auf das Mikrofon, um Sprachnachrichten zu senden.",
    "assistant.microphoneDenied": "Mikrofon-Zugriff wurde verweigert. Bitte erlaube den Zugriff in deinen Browser-Einstellungen.",
    "assistant.microphoneNotFound": "Kein Mikrofon gefunden. Bitte stelle sicher, dass ein Mikrofon angeschlossen ist.",
    "assistant.microphoneAccessFailed": "Mikrofon-Zugriff fehlgeschlagen",
    "commentModal.close": "Schliessen",
    "commentModal.addTitle": "Kommentar hinzufugen",
    "commentModal.editTitle": "Kommentar bearbeiten",
    "commentModal.placeholder": "Schreib hier deinen Kommentar...",
    "commentModal.characters": "{count}/1000 Zeichen",
    "commentModal.save": "Kommentar speichern",
    "profile.invalidTypeTitle": "Ungultiger Dateityp",
    "profile.invalidTypeDescription": "Bitte wahle eine Bilddatei aus.",
    "profile.fileTooLargeTitle": "Datei zu gross",
    "profile.fileTooLargeDescription": "Bitte wahle ein Bild kleiner als 10MB aus.",
    "profile.imageUpdated": "Profilbild aktualisiert",
    "profile.imageUpdatedDescription": "Dein Profilbild wurde erfolgreich aktualisiert.",
    "profile.uploadFailed": "Upload fehlgeschlagen",
    "profile.uploadFailedDescription": "Profilbild konnte nicht hochgeladen werden. Bitte versuche es erneut.",
    "profile.updated": "Profil aktualisiert",
    "profile.updatedDescription": "Deine Profildaten wurden erfolgreich gespeichert.",
    "profile.updateFailed": "Profil konnte nicht aktualisiert werden",
    "profile.updateFailedDescription": "Bitte versuche es erneut.",
    "profile.firstName": "Vorname",
    "profile.lastName": "Nachname",
    "profile.uploadImage": "Bild hochladen",
    "profile.dragDrop": "Bild hierher ziehen oder klicken zum Auswahlen",
    "profile.cropImage": "Bild zuschneiden",
    "profile.cropHint": "Verschiebe und zoome das Bild fur den perfekten Ausschnitt.",
    "profile.saveChanges": "Anderungen speichern",
    "settings.title": "Einstellungen",
    "settings.subtitle": "Profil und App-Konfiguration.",
    "settings.appConfig": "App-Konfiguration",
    "settings.loading": "Einstellungen werden geladen...",
    "settings.assistantName": "Assistentenname",
    "settings.assistantNamePlaceholder": "z. B. Bobee",
    "settings.welcomeMessage": "Willkommensnachricht",
    "settings.welcomePlaceholder": "Verwende {ASSISTANT_NAME} als Platzhalter.",
    "settings.assistantPhoto": "Assistentenfoto",
    "settings.clickPhoto": "Auf Foto klicken zum Andern",
    "settings.removePhoto": "Foto entfernen",
    "documents.title": "Dokumente",
    "documents.folderStructure": "Ordnerstruktur",
    "documents.currentFolder": "Aktuell",
    "documents.newFolder": "Neuer Ordner",
    "documents.deleteFolder": "Ordner loschen",
    "documents.uploadedDocuments": "Hochgeladene Dokumente",
    "documents.document_one": "Dokument",
    "documents.document_other": "Dokumente",
    "documents.searchPlaceholder": "Dokumente suchen...",
    "documents.upload": "Hochladen",
    "documents.uploadNew": "Neu hochladen",
    "documents.folder": "Ordner",
    "documents.filename": "Dateiname",
    "documents.uploadedBy": "Hochgeladen von",
    "documents.datetime": "Datum/Zeit",
    "documents.categories": "Kategorien",
    "documents.status": "Status",
    "documents.actions": "Aktionen",
    "documents.subfolder": "Unterordner",
    "documents.open": "Offnen",
    "documents.loading": "Dokumente werden geladen...",
    "documents.empty": "Keine Dokumente gefunden",
    "documents.showingResults": "Zeige {shown} von {total} Ergebnissen",
    "documents.allOnOnePage": "Alle Dokumente auf einer Seite ({count} {label})",
    "documents.pageOf": "Seite {current} von {total}",
    "documents.pending": "Ausstehend",
    "documents.processing": "Wird verarbeitet",
    "documents.failed": "Fehlgeschlagen",
    "documents.permanentFailed": "Dauerhaft fehlgeschlagen",
    "documents.processed": "Verarbeitet",
    "documents.processingCount": "{count} werden verarbeitet",
    "documents.processingCountParallel": "{count} werden parallel verarbeitet",
    "documents.unknown": "Unbekannt",
    "documents.deleteSuccess": "Loschen erfolgreich",
    "documents.deleteSuccessDescription": "Das Dokument wurde geloscht.",
    "documents.deleteFailed": "Loschen fehlgeschlagen",
    "documents.deleteFailedDescription": "Das Dokument konnte nicht geloscht werden.",
    "documents.openFailed": "Offnen fehlgeschlagen",
    "documents.openFailedDescription": "Dokument \"{name}\" konnte nicht geoffnet werden.",
    "documents.uploadPartialFailed": "Upload teilweise fehlgeschlagen",
    "documents.uploadPartialFailedDescription": "{count} Datei(en) konnten nicht hochgeladen werden: {failedNames}",
    "documents.uploadSuccess": "Upload erfolgreich",
    "documents.uploadSuccessDescription": "{count} Datei(en) wurden hochgeladen. Verarbeitung lauft.",
    "documents.uploadFailed": "Upload fehlgeschlagen",
    "documents.categoryRequired": "Kategorie erforderlich",
    "documents.categoryRequiredDescription": "Bitte wahle mindestens eine Kategorie aus deinen Rollen.",
    "documents.invalidFileType": "Ungultiger Dateityp",
    "documents.invalidFileTypeDescription": "Nur {extensions} Dateien sind erlaubt.",
    "documents.fileTooLarge": "Datei zu gross",
    "documents.fileTooLargeDescription": "Folgende Dateien sind zu gross (max 25MB, IFC bis 500MB): {files}",
    "documents.totalSizeExceeded": "Gesamtgrosse uberschritten",
    "documents.totalSizeExceededDescription": "Gesamtgrosse uberschreitet {limit}MB Limit.",
    "documents.folderCreated": "Ordner erstellt",
    "documents.folderCreatedDescription": "Ordner \"{name}\" wurde erstellt.",
    "documents.folderCreateFailed": "Ordner konnte nicht erstellt werden",
    "documents.folderDeleted": "Ordner geloscht",
    "documents.folderDeletedDescription": "Ordner \"{name}\" wurde geloscht.",
    "documents.folderDeleteFailed": "Ordner konnte nicht geloscht werden",
    "documents.folderDeleteConfirm": "Ordner \"{name}\" wirklich loschen?",
    "documents.createFolder": "Neuen Ordner erstellen",
    "documents.folderName": "Ordnername",
    "documents.folderNamePlaceholder": "z. B. Vertrage 2026",
    "documents.parentFolder": "Parent-Ordner",
    "documents.topLevel": "Top-Level",
    "documents.create": "Erstellen",
    "documents.uploadDocumentNew": "Neues Dokument hochladen",
    "documents.uploadDocumentSingle": "1 Dokument hochladen",
    "documents.uploadDocumentMulti": "{count} Dokumente hochladen",
    "documents.selectFiles": "Datei(en) auswahlen",
    "documents.filesSelected": "{count} Datei(en) ausgewahlt:",
    "documents.removeAll": "Alle entfernen",
    "documents.dropFiles": "Datei(en) hier hineinziehen oder zum Durchsuchen klicken",
    "documents.browseFiles": "Dateien durchsuchen",
    "documents.categoriesRequired": "Kategorien (erforderlich)",
    "documents.categoriesOptional": "Kategorien (optional)",
    "documents.noAllowedCategories": "Keine erlaubten Kategorien in deiner Session gefunden. Upload ist gesperrt.",
    "documents.supportedFormats": "Unterstutzte Formate: PDF, DOCX, XLSX. Max 25MB pro Datei, 100MB insgesamt. Die Verarbeitung kann mehrere Minuten dauern.",
    "documents.uploadingFiles": "Wird hochgeladen... ({count} Datei{suffix})",
    "documents.uploadSingleFile": "1 Datei hochladen",
    "documents.uploadMultiFiles": "{count} Dateien hochladen",
    "responses.editing": "Bearbeitung",
    "responses.edited": "Bearbeitet",
    "responses.notEdited": "Nicht bearbeitet",
    "responses.userFeedback": "Benutzer-Feedback",
    "responses.adminFeedback": "Admin-Feedback",
    "responses.comments": "Kommentare",
    "responses.withComments": "Mit Kommentaren",
    "responses.withoutComments": "Ohne Kommentare",
    "responses.archiving": "Archivierung",
    "responses.archived": "Archiviert",
    "responses.notArchived": "Nicht archiviert",
    "responses.allUsers": "Alle Benutzer",
    "responses.loadingUsers": "Lade Benutzer...",
    "responses.unknownUser": "Unbekannter Benutzer",
    "responses.unknownContact": "Unbekannter Kontakt",
    "responses.allQrCodes": "Alle QR Codes",
    "responses.loadingQrCodes": "Lade QR Codes...",
    "responses.messagesCount": "{count} Nachrichten",
    "responses.newestFirst": "Neueste zuerst",
    "responses.oldestFirst": "Alteste zuerst",
    "responses.refreshing": "Wird aktualisiert...",
    "responses.refresh": "Aktualisieren",
    "responses.empty": "Keine Nachrichten entsprechen den aktuellen Filtern.",
    "responses.versions": "Versionen",
    "responses.edit": "Bearbeiten",
    "responses.originalAnswer": "Originale Antwort",
    "responses.editedAnswer": "Bearbeitete Antwort",
    "responses.userComment": "Benutzerkommentar:",
    "responses.good": "Gut",
    "responses.bad": "Schlecht",
    "responses.ratedBy": "Bewertet von {email}",
    "responses.correctionTitle": "Korrektur eingeben",
    "responses.correctionDescription": "Bitte gib die korrekte Antwort ein, damit die KI daraus lernen kann.",
    "responses.currentAnswer": "Aktuelle Antwort:",
    "responses.correctedAnswer": "Korrigierte Antwort:",
    "responses.correctedAnswerPlaceholder": "Gib hier die korrekte Antwort ein...",
    "responses.saveCorrectionFailed": "Fehler beim Speichern der Korrektur.",
    "settings.invalidType": "Ungultiger Dateityp",
    "settings.invalidTypeDescription": "Bitte wahle eine Bilddatei aus.",
    "settings.fileTooLarge": "Datei zu gross",
    "settings.fileTooLargeDescription": "Bitte wahle ein Bild kleiner als 10MB aus.",
    "settings.uploadFailed": "Upload fehlgeschlagen",
    "settings.uploadFailedDescription": "Das Bild konnte nicht gelesen werden.",
    "settings.nameMissing": "Assistentenname fehlt",
    "settings.nameMissingDescription": "Bitte gib einen Assistentennamen ein.",
    "settings.welcomeMissing": "Willkommensnachricht fehlt",
    "settings.welcomeMissingDescription": "Bitte gib eine Willkommensnachricht ein.",
    "settings.saved": "Einstellungen gespeichert",
    "settings.savedDescription": "Assistent-Konfiguration wurde aktualisiert.",
    "settings.saveFailed": "Speichern fehlgeschlagen",
    "settings.unknownError": "Unbekannter Fehler",
    "users.title": "Benutzer",
    "users.allUsers": "Alle Benutzer",
    "users.count_one": "{count} Benutzer",
    "users.count_other": "{count} Benutzer",
    "users.searchPlaceholder": "Benutzer suchen...",
    "users.loading": "Benutzer werden geladen...",
    "users.empty": "Keine Benutzer gefunden",
    "users.noCategories": "Keine Kategorien",
    "users.less": "weniger",
    "users.fetchFailed": "Benutzer konnten nicht geladen werden",
    "users.lastAdminError": "Du kannst die Rolle des letzten Administrators nicht andern",
    "users.roleChanged": "Rolle erfolgreich geandert. Benutzer muss sich erneut anmelden.",
    "users.changeOwnRoleError": "Du kannst deine eigene Rolle nicht andern",
    "users.changeRoleFailed": "Fehler beim Andern der Rolle",
    "users.role": "Rolle",
    "users.categories": "Kategorien",
    "users.addedOn": "Hinzugefugt am",
    "analytics.title": "Analytics Dashboard",
    "analytics.subtitle": "Nutzung, Interaktionen und Feedback auf einen Blick.",
    "analytics.mobileSubtitle": "Ubersicht und KPIs",
    "analytics.invalidRange": "Startdatum muss vor dem Enddatum liegen.",
    "analytics.loadFailed": "Analytics konnten nicht geladen werden.",
    "analytics.interval.day": "Taglich",
    "analytics.interval.week": "Wochentlich",
    "analytics.interval.month": "Monatlich",
    "analytics.activeUsers": "Aktive Nutzer",
    "analytics.activeUsersHint": "Eindeutige Nutzer im Zeitraum",
    "analytics.interactions": "Interaktionen",
    "analytics.interactionsHint": "Gestellte Fragen und Antworten",
    "analytics.feedback": "Feedback",
    "analytics.feedbackHint": "Abgegebene Bewertungen insgesamt",
    "analytics.positiveRate": "Positivquote",
    "analytics.positiveRateHint": "Verhaltnis Likes zu Gesamtfeedback",
    "analytics.usageOverTime": "Nutzung uber Zeit",
    "analytics.usageOverTimeHint": "Aktive Nutzer und Interaktionen pro Zeitraum.",
    "analytics.chartLoading": "Lade Diagramm...",
    "analytics.noUsageData": "Noch keine Daten im ausgewahlten Zeitraum.",
    "analytics.feedbackTrend": "Feedback-Entwicklung",
    "analytics.feedbackTrendHint": "Positive vs. negative Bewertungen pro Zeitraum.",
    "analytics.noFeedbackData": "Keine Feedback-Daten im Zeitraum.",
    "analytics.perUser": "Interaktionen pro Nutzer",
    "analytics.perUserHint": "Alle Nutzer mit Interaktionen im gewahlten Zeitraum.",
    "analytics.userDataLoading": "Lade Nutzerdaten...",
    "analytics.user": "Nutzer",
    "analytics.lastActive": "Zuletzt aktiv",
    "analytics.noInteractions": "Keine Interaktionen in diesem Zeitraum.",
    "analytics.showingUsers": "Zeige {shown} von {total} Nutzern",
    "analytics.perPage": "{count} / Seite",
    "analytics.allOnOnePage": "Alle Nutzer auf einer Seite ({count} Nutzer)",
    "analytics.sortAsc": "{field} aufsteigend sortieren",
    "analytics.sortDesc": "{field} absteigend sortieren",
  },
  en: {},
  fr: {},
  it: {},
};

translations.en = {
  ...translations.de,
  "language.label": "Language",
  "language.de": "German",
  "language.en": "English",
  "language.fr": "French",
  "language.it": "Italian",
  "common.settings": "Settings",
  "common.profile": "Edit profile",
  "common.cancel": "Cancel",
  "common.save": "Save",
  "common.saving": "Saving...",
  "common.loading": "Loading...",
  "common.all": "All",
  "common.none": "None",
  "common.search": "Search",
  "common.delete": "Delete",
  "common.edit": "Edit",
  "common.logout": "Log out",
  "common.you": "You",
  "common.from": "From",
  "common.to": "To",
  "common.interval": "Interval",
  "common.previous": "Previous",
  "common.next": "Next",
  "nav.assistant": "Knowledge assistant",
  "nav.documents": "Documents",
  "nav.users": "Users",
  "nav.responses": "Generated answers",
  "settingsDropdown.logoName": "Change logo name",
  "settingsDropdown.newLogoName": "New logo name",
  "settingsDropdown.color": "Change color",
  "settingsDropdown.chooseColor": "Choose color",
  "settingsDropdown.switchToAdmin": "Switch to admin",
  "settingsDropdown.memberAssistant": "Knowledge assistant",
  "memberSidebar.newChat": "Start new chat",
  "memberSidebar.assistant": "Assistant",
  "memberSidebar.noThreads": "No conversations yet",
  "memberSidebar.loadMore": "Load more...",
  "memberSidebar.threadActions": "Thread actions",
  "memberSidebar.today": "TODAY",
  "memberSidebar.yesterday": "YESTERDAY",
  "memberSidebar.previous7": "PREVIOUS 7 DAYS",
  "memberSidebar.previous30": "PREVIOUS 30 DAYS",
  "memberSidebar.olderThan30": "MORE THAN 30 DAYS",
  "memberSidebar.archiveTitle": "Archive thread",
  "memberSidebar.archiveConfirm": "Do you really want to archive this thread? It will disappear from your view but remain visible to administrators.",
  "memberSidebar.archive": "Archive",
  "assistant.errorGeneric": "Sorry, something went wrong. Please try again later.",
  "assistant.feedbackTooltip": "Your feedback is reviewed by the team. Ratings help us spot incorrect answers and improve the underlying documents.",
  "assistant.copySuccess": "Copied to clipboard",
  "assistant.copyTooltip": "Copy the answer without the sources section.",
  "assistant.commentAdd": "Add a comment for the team, for example what is wrong or missing.",
  "assistant.commentEdit": "Edit the comment for the team, for example what is wrong or missing.",
  "assistant.regenerate": "Generate a new answer to the same question.",
  "assistant.yourComment": "Your comment:",
  "assistant.deleteComment": "Delete comment",
  "assistant.webPlaceholder": "Search the web... (press Enter to send)",
  "assistant.askPlaceholder": "Ask me something... (press Enter to send)",
  "assistant.switchToWeb": "Switch to web search",
  "assistant.switchToDocs": "Switch to document search",
  "assistant.stopRecording": "Stop recording",
  "assistant.transcribing": "Transcribing...",
  "assistant.micUnsupported": "Voice recording is not supported in this browser",
  "assistant.startRecording": "Start voice recording",
  "assistant.askButton": "Ask",
  "assistant.transcriptionFailed": "Transcription failed",
  "assistant.transcriptionNoText": "Speech recognition could not detect any text.",
  "assistant.transcriptionError": "There was an error during speech recognition.",
  "assistant.transcriptionTooShort": "The recording was too short or empty. Please try again.",
  "assistant.transcriptionNetwork": "Network error during speech recognition. Please check your internet connection.",
  "assistant.commentSaved": "Comment saved",
  "assistant.commentSaveFailed": "Could not save comment",
  "assistant.commentDeleted": "Comment deleted",
  "assistant.commentDeleteFailed": "Could not delete comment",
  "assistant.microphonePermission": "Please allow microphone access to send voice messages.",
  "assistant.microphoneDenied": "Microphone access was denied. Please allow access in your browser settings.",
  "assistant.microphoneNotFound": "No microphone was found. Make sure a microphone is connected.",
  "assistant.microphoneAccessFailed": "Microphone access failed",
  "commentModal.close": "Close",
  "commentModal.addTitle": "Add comment",
  "commentModal.editTitle": "Edit comment",
  "commentModal.placeholder": "Write your comment here...",
  "commentModal.characters": "{count}/1000 characters",
  "commentModal.save": "Save comment",
  "profile.invalidTypeTitle": "Invalid file type",
  "profile.invalidTypeDescription": "Please select an image file.",
  "profile.fileTooLargeTitle": "File too large",
  "profile.fileTooLargeDescription": "Please choose an image smaller than 10MB.",
  "profile.imageUpdated": "Profile image updated",
  "profile.imageUpdatedDescription": "Your profile image was updated successfully.",
  "profile.uploadFailed": "Upload failed",
  "profile.uploadFailedDescription": "The profile image could not be uploaded. Please try again.",
  "profile.updated": "Profile updated",
  "profile.updatedDescription": "Your profile details were saved successfully.",
  "profile.updateFailed": "Profile could not be updated",
  "profile.updateFailedDescription": "Please try again.",
  "profile.firstName": "First name",
  "profile.lastName": "Last name",
  "profile.uploadImage": "Upload image",
  "profile.dragDrop": "Drag an image here or click to choose one",
  "profile.cropImage": "Crop image",
  "profile.cropHint": "Move and zoom the image to get the right crop.",
  "profile.saveChanges": "Save changes",
  "settings.title": "Settings",
  "settings.subtitle": "Profile and app configuration.",
  "settings.appConfig": "App configuration",
  "settings.loading": "Loading settings...",
  "settings.assistantName": "Assistant name",
  "settings.assistantNamePlaceholder": "e.g. Bobee",
  "settings.welcomeMessage": "Welcome message",
  "settings.welcomePlaceholder": "Use {ASSISTANT_NAME} as a placeholder.",
  "settings.assistantPhoto": "Assistant photo",
  "settings.clickPhoto": "Click the photo to change it",
  "settings.removePhoto": "Remove photo",
  "documents.title": "Documents",
  "documents.folderStructure": "Folder structure",
  "documents.currentFolder": "Current",
  "documents.newFolder": "New folder",
  "documents.deleteFolder": "Delete folder",
  "documents.uploadedDocuments": "Uploaded documents",
  "documents.document_one": "document",
  "documents.document_other": "documents",
  "documents.searchPlaceholder": "Search documents...",
  "documents.upload": "Upload",
  "documents.uploadNew": "Upload new",
  "documents.folder": "Folder",
  "documents.filename": "Filename",
  "documents.uploadedBy": "Uploaded by",
  "documents.datetime": "Date/Time",
  "documents.categories": "Categories",
  "documents.status": "Status",
  "documents.actions": "Actions",
  "documents.subfolder": "Subfolder",
  "documents.open": "Open",
  "documents.loading": "Loading documents...",
  "documents.empty": "No documents found",
  "documents.showingResults": "Showing {shown} of {total} results",
  "documents.allOnOnePage": "All documents on one page ({count} {label})",
  "documents.pageOf": "Page {current} of {total}",
  "documents.pending": "Pending",
  "documents.processing": "Processing",
  "documents.failed": "Failed",
  "documents.permanentFailed": "Permanently failed",
  "documents.processed": "Processed",
  "documents.processingCount": "{count} processing",
  "documents.processingCountParallel": "{count} processing in parallel",
  "documents.unknown": "Unknown",
  "documents.deleteSuccess": "Delete successful",
  "documents.deleteSuccessDescription": "The document was deleted.",
  "documents.deleteFailed": "Delete failed",
  "documents.deleteFailedDescription": "The document could not be deleted.",
  "documents.openFailed": "Open failed",
  "documents.openFailedDescription": "Document \"{name}\" could not be opened.",
  "documents.uploadPartialFailed": "Upload partially failed",
  "documents.uploadPartialFailedDescription": "{count} file(s) could not be uploaded: {failedNames}",
  "documents.uploadSuccess": "Upload successful",
  "documents.uploadSuccessDescription": "{count} file(s) were uploaded. Processing is running.",
  "documents.uploadFailed": "Upload failed",
  "documents.categoryRequired": "Category required",
  "documents.categoryRequiredDescription": "Please select at least one category from your roles.",
  "documents.invalidFileType": "Invalid file type",
  "documents.invalidFileTypeDescription": "Only {extensions} files are allowed.",
  "documents.fileTooLarge": "File too large",
  "documents.fileTooLargeDescription": "These files are too large (max 25MB, IFC up to 500MB): {files}",
  "documents.totalSizeExceeded": "Total size exceeded",
  "documents.totalSizeExceededDescription": "Total size exceeds the {limit}MB limit.",
  "documents.folderCreated": "Folder created",
  "documents.folderCreatedDescription": "Folder \"{name}\" was created.",
  "documents.folderCreateFailed": "Folder could not be created",
  "documents.folderDeleted": "Folder deleted",
  "documents.folderDeletedDescription": "Folder \"{name}\" was deleted.",
  "documents.folderDeleteFailed": "Folder could not be deleted",
  "documents.folderDeleteConfirm": "Delete folder \"{name}\"?",
  "documents.createFolder": "Create new folder",
  "documents.folderName": "Folder name",
  "documents.folderNamePlaceholder": "e.g. Contracts 2026",
  "documents.parentFolder": "Parent folder",
  "documents.topLevel": "Top level",
  "documents.create": "Create",
  "documents.uploadDocumentNew": "Upload new document",
  "documents.uploadDocumentSingle": "Upload 1 document",
  "documents.uploadDocumentMulti": "Upload {count} documents",
  "documents.selectFiles": "Select file(s)",
  "documents.filesSelected": "{count} file(s) selected:",
  "documents.removeAll": "Remove all",
  "documents.dropFiles": "Drop files here or click to browse",
  "documents.browseFiles": "Browse files",
  "documents.categoriesRequired": "Categories (required)",
  "documents.categoriesOptional": "Categories (optional)",
  "documents.noAllowedCategories": "No allowed categories found in your session. Upload is blocked.",
  "documents.supportedFormats": "Supported formats: PDF, DOCX, XLSX. Max 25MB per file, 100MB total. Processing can take several minutes.",
  "documents.uploadingFiles": "Uploading... ({count} file{suffix})",
  "documents.uploadSingleFile": "Upload 1 file",
  "documents.uploadMultiFiles": "Upload {count} files",
  "responses.editing": "Editing",
  "responses.edited": "Edited",
  "responses.notEdited": "Not edited",
  "responses.userFeedback": "User feedback",
  "responses.adminFeedback": "Admin feedback",
  "responses.comments": "Comments",
  "responses.withComments": "With comments",
  "responses.withoutComments": "Without comments",
  "responses.archiving": "Archiving",
  "responses.archived": "Archived",
  "responses.notArchived": "Not archived",
  "responses.allUsers": "All users",
  "responses.loadingUsers": "Loading users...",
  "responses.unknownUser": "Unknown user",
  "responses.unknownContact": "Unknown contact",
  "responses.allQrCodes": "All QR codes",
  "responses.loadingQrCodes": "Loading QR codes...",
  "responses.messagesCount": "{count} messages",
  "responses.newestFirst": "Newest first",
  "responses.oldestFirst": "Oldest first",
  "responses.refreshing": "Refreshing...",
  "responses.refresh": "Refresh",
  "responses.empty": "No messages match the current filters.",
  "responses.versions": "Versions",
  "responses.edit": "Edit",
  "responses.originalAnswer": "Original answer",
  "responses.editedAnswer": "Edited answer",
  "responses.userComment": "User comment:",
  "responses.good": "Good",
  "responses.bad": "Bad",
  "responses.ratedBy": "Rated by {email}",
  "responses.correctionTitle": "Enter correction",
  "responses.correctionDescription": "Please enter the correct answer so the AI can learn from it.",
  "responses.currentAnswer": "Current answer:",
  "responses.correctedAnswer": "Corrected answer:",
  "responses.correctedAnswerPlaceholder": "Enter the correct answer here...",
  "responses.saveCorrectionFailed": "Failed to save correction.",
  "settings.invalidType": "Invalid file type",
  "settings.invalidTypeDescription": "Please select an image file.",
  "settings.fileTooLarge": "File too large",
  "settings.fileTooLargeDescription": "Please choose an image smaller than 10MB.",
  "settings.uploadFailed": "Upload failed",
  "settings.uploadFailedDescription": "The image could not be read.",
  "settings.nameMissing": "Assistant name is missing",
  "settings.nameMissingDescription": "Please enter an assistant name.",
  "settings.welcomeMissing": "Welcome message is missing",
  "settings.welcomeMissingDescription": "Please enter a welcome message.",
  "settings.saved": "Settings saved",
  "settings.savedDescription": "Assistant configuration was updated.",
  "settings.saveFailed": "Save failed",
  "settings.unknownError": "Unknown error",
  "users.title": "Users",
  "users.allUsers": "All users",
  "users.count_one": "{count} user",
  "users.count_other": "{count} users",
  "users.searchPlaceholder": "Search users...",
  "users.loading": "Loading users...",
  "users.empty": "No users found",
  "users.noCategories": "No categories",
  "users.less": "less",
  "users.fetchFailed": "Failed to load users",
  "users.lastAdminError": "You cannot change the role of the last administrator",
  "users.roleChanged": "Role updated successfully. The user must sign in again.",
  "users.changeOwnRoleError": "You cannot change your own role",
  "users.changeRoleFailed": "Failed to change role",
  "users.role": "Role",
  "users.categories": "Categories",
  "users.addedOn": "Added on",
  "analytics.title": "Analytics Dashboard",
  "analytics.subtitle": "Usage, interactions, and feedback at a glance.",
  "analytics.mobileSubtitle": "Overview and KPIs",
  "analytics.invalidRange": "Start date must be before the end date.",
  "analytics.loadFailed": "Could not load analytics.",
  "analytics.interval.day": "Daily",
  "analytics.interval.week": "Weekly",
  "analytics.interval.month": "Monthly",
  "analytics.activeUsers": "Active users",
  "analytics.activeUsersHint": "Unique users in the selected period",
  "analytics.interactions": "Interactions",
  "analytics.interactionsHint": "Questions asked and answers returned",
  "analytics.feedback": "Feedback",
  "analytics.feedbackHint": "Total ratings submitted",
  "analytics.positiveRate": "Positive rate",
  "analytics.positiveRateHint": "Share of likes across all feedback",
  "analytics.usageOverTime": "Usage over time",
  "analytics.usageOverTimeHint": "Active users and interactions per period.",
  "analytics.chartLoading": "Loading chart...",
  "analytics.noUsageData": "No data in the selected period yet.",
  "analytics.feedbackTrend": "Feedback trend",
  "analytics.feedbackTrendHint": "Positive vs. negative ratings per period.",
  "analytics.noFeedbackData": "No feedback data in this period.",
  "analytics.perUser": "Interactions per user",
  "analytics.perUserHint": "All users with interactions in the selected period.",
  "analytics.userDataLoading": "Loading user data...",
  "analytics.user": "User",
  "analytics.lastActive": "Last active",
  "analytics.noInteractions": "No interactions in this period.",
  "analytics.showingUsers": "Showing {shown} of {total} users",
  "analytics.perPage": "{count} / page",
  "analytics.allOnOnePage": "All users on one page ({count} users)",
  "analytics.sortAsc": "Sort {field} ascending",
  "analytics.sortDesc": "Sort {field} descending",
};

translations.fr = {
  ...translations.en,
  "language.label": "Langue",
  "language.de": "Allemand",
  "language.en": "Anglais",
  "language.fr": "Francais",
  "language.it": "Italien",
  "common.settings": "Parametres",
  "common.profile": "Modifier le profil",
  "common.cancel": "Annuler",
  "common.save": "Enregistrer",
  "common.saving": "Enregistrement...",
  "common.loading": "Chargement...",
  "common.all": "Tous",
  "common.none": "Aucun",
  "common.search": "Rechercher",
  "common.delete": "Supprimer",
  "common.edit": "Modifier",
  "common.logout": "Se deconnecter",
  "common.you": "Vous",
  "nav.documents": "Documents",
  "nav.users": "Utilisateurs",
  "nav.responses": "Reponses generees",
  "nav.analytics": "Analyses",
  "nav.assistant": "Assistant de connaissance",
  "settingsDropdown.logoName": "Changer le nom du logo",
  "settingsDropdown.newLogoName": "Nouveau nom du logo",
  "settingsDropdown.color": "Changer la couleur",
  "settingsDropdown.chooseColor": "Choisir une couleur",
  "settingsDropdown.switchToAdmin": "Passer a l'admin",
  "settingsDropdown.language": "Langue",
  "settingsDropdown.memberAssistant": "Assistant de connaissance",
  "memberSidebar.pageTitle": "{appName} Assistant de connaissance",
  "memberSidebar.newChat": "Demarrer un nouveau chat",
  "memberSidebar.assistant": "Assistant",
  "memberSidebar.noThreads": "Aucune conversation pour le moment",
  "memberSidebar.loadMore": "Charger plus...",
  "memberSidebar.threadActions": "Actions du fil",
  "memberSidebar.today": "AUJOURD'HUI",
  "memberSidebar.yesterday": "HIER",
  "memberSidebar.previous7": "7 DERNIERS JOURS",
  "memberSidebar.previous30": "30 DERNIERS JOURS",
  "memberSidebar.olderThan30": "PLUS DE 30 JOURS",
  "memberSidebar.archiveTitle": "Archiver le fil",
  "memberSidebar.archiveConfirm": "Voulez-vous vraiment archiver ce fil ? Il disparaitra de votre vue mais restera visible pour les administrateurs.",
  "memberSidebar.archive": "Archiver",
  "assistant.errorGeneric": "Desole, une erreur s'est produite. Veuillez reessayer plus tard.",
  "assistant.feedbackTooltip": "Votre retour est examine par l'equipe. Les evaluations nous aident a reperer les mauvaises reponses et a ameliorer les documents sous-jacents.",
  "assistant.copySuccess": "Copie dans le presse-papiers",
  "assistant.copyTooltip": "Copier la reponse sans la section des sources.",
  "assistant.commentAdd": "Ajouter un commentaire pour l'equipe, par exemple ce qui est faux ou manquant.",
  "assistant.commentEdit": "Modifier le commentaire pour l'equipe, par exemple ce qui est faux ou manquant.",
  "assistant.regenerate": "Generer une nouvelle reponse a la meme question.",
  "assistant.yourComment": "Votre commentaire :",
  "assistant.deleteComment": "Supprimer le commentaire",
  "assistant.webPlaceholder": "Rechercher sur le web... (Entree pour envoyer)",
  "assistant.askPlaceholder": "Posez une question... (Entree pour envoyer)",
  "assistant.switchToWeb": "Passer a la recherche web",
  "assistant.switchToDocs": "Passer a la recherche documentaire",
  "assistant.stopRecording": "Arreter l'enregistrement",
  "assistant.transcribing": "Transcription en cours...",
  "assistant.micUnsupported": "L'enregistrement vocal n'est pas pris en charge dans ce navigateur",
  "assistant.startRecording": "Demarrer l'enregistrement vocal",
  "assistant.askButton": "Demander",
  "assistant.transcriptionFailed": "Echec de la transcription",
  "assistant.transcriptionNoText": "La reconnaissance vocale n'a detecte aucun texte.",
  "assistant.transcriptionError": "Une erreur s'est produite pendant la reconnaissance vocale.",
  "assistant.transcriptionTooShort": "L'enregistrement etait trop court ou vide. Veuillez reessayer.",
  "assistant.transcriptionNetwork": "Erreur reseau pendant la reconnaissance vocale. Verifiez votre connexion internet.",
  "assistant.commentSaved": "Commentaire enregistre",
  "assistant.commentSaveFailed": "Impossible d'enregistrer le commentaire",
  "assistant.commentDeleted": "Commentaire supprime",
  "assistant.commentDeleteFailed": "Impossible de supprimer le commentaire",
  "assistant.microphonePermission": "Veuillez autoriser l'acces au microphone pour envoyer des messages vocaux.",
  "assistant.microphoneDenied": "L'acces au microphone a ete refuse. Veuillez l'autoriser dans les parametres de votre navigateur.",
  "assistant.microphoneNotFound": "Aucun microphone n'a ete trouve. Verifiez qu'un microphone est connecte.",
  "assistant.microphoneAccessFailed": "Echec d'acces au microphone",
  "settings.removePhoto": "Supprimer la photo",
  "documents.folderStructure": "Structure des dossiers",
  "documents.currentFolder": "Actuel",
  "documents.newFolder": "Nouveau dossier",
  "documents.deleteFolder": "Supprimer le dossier",
  "documents.uploadedDocuments": "Documents televerses",
  "documents.searchPlaceholder": "Rechercher des documents...",
  "documents.upload": "Televerser",
  "documents.uploadNew": "Nouveau televersement",
  "documents.folder": "Dossier",
  "documents.filename": "Nom du fichier",
  "documents.uploadedBy": "Televerse par",
  "documents.datetime": "Date/Heure",
  "documents.categories": "Categories",
  "documents.status": "Statut",
  "documents.actions": "Actions",
  "documents.subfolder": "Sous-dossier",
  "documents.open": "Ouvrir",
  "documents.loading": "Chargement des documents...",
  "documents.empty": "Aucun document trouve",
  "documents.pageOf": "Page {current} sur {total}",
  "documents.pending": "En attente",
  "documents.processing": "Traitement en cours",
  "documents.failed": "Echec",
  "documents.permanentFailed": "Echec permanent",
  "documents.processed": "Traite",
  "documents.createFolder": "Creer un dossier",
  "documents.folderName": "Nom du dossier",
  "documents.parentFolder": "Dossier parent",
  "documents.topLevel": "Niveau racine",
  "documents.create": "Creer",
  "documents.selectFiles": "Selectionner des fichiers",
  "documents.removeAll": "Tout supprimer",
  "documents.browseFiles": "Parcourir les fichiers",
  "documents.categoriesRequired": "Categories (obligatoires)",
  "documents.categoriesOptional": "Categories (optionnelles)",
  "documents.supportedFormats": "Formats pris en charge : PDF, DOCX, XLSX. Taille max. 25 Mo par fichier, 100 Mo au total. Le traitement peut prendre plusieurs minutes.",
  "documents.title": "Documents",
  "responses.editing": "Edition",
  "responses.userFeedback": "Avis utilisateur",
  "responses.adminFeedback": "Avis admin",
  "responses.comments": "Commentaires",
  "responses.withComments": "Avec commentaires",
  "responses.withoutComments": "Sans commentaires",
  "responses.archiving": "Archivage",
  "responses.archived": "Archive",
  "responses.notArchived": "Non archive",
  "responses.allUsers": "Tous les utilisateurs",
  "responses.loadingUsers": "Chargement des utilisateurs...",
  "responses.unknownUser": "Utilisateur inconnu",
  "responses.unknownContact": "Contact inconnu",
  "responses.allQrCodes": "Tous les codes QR",
  "responses.loadingQrCodes": "Chargement des codes QR...",
  "responses.newestFirst": "Plus recent d'abord",
  "responses.oldestFirst": "Plus ancien d'abord",
  "responses.refreshing": "Actualisation...",
  "responses.refresh": "Actualiser",
  "responses.empty": "Aucun message ne correspond aux filtres actuels.",
  "responses.versions": "Versions",
  "responses.edit": "Modifier",
  "responses.originalAnswer": "Reponse originale",
  "responses.editedAnswer": "Reponse modifiee",
  "responses.userComment": "Commentaire utilisateur :",
  "responses.good": "Bon",
  "responses.bad": "Mauvais",
  "responses.correctionTitle": "Saisir une correction",
  "responses.correctionDescription": "Veuillez saisir la bonne reponse afin que l'IA puisse apprendre.",
  "responses.currentAnswer": "Reponse actuelle :",
  "responses.correctedAnswer": "Reponse corrigee :",
  "settings.title": "Parametres",
  "users.title": "Utilisateurs",
};

translations.it = {
  ...translations.en,
  "language.label": "Lingua",
  "language.de": "Tedesco",
  "language.en": "Inglese",
  "language.fr": "Francese",
  "language.it": "Italiano",
  "common.settings": "Impostazioni",
  "common.profile": "Modifica profilo",
  "common.cancel": "Annulla",
  "common.save": "Salva",
  "common.saving": "Salvataggio...",
  "common.loading": "Caricamento...",
  "common.all": "Tutti",
  "common.none": "Nessuno",
  "common.search": "Cerca",
  "common.delete": "Elimina",
  "common.edit": "Modifica",
  "common.logout": "Disconnetti",
  "common.you": "Tu",
  "nav.documents": "Documenti",
  "nav.users": "Utenti",
  "nav.responses": "Risposte generate",
  "nav.analytics": "Analitiche",
  "nav.assistant": "Assistente della conoscenza",
  "settingsDropdown.logoName": "Cambia nome logo",
  "settingsDropdown.newLogoName": "Nuovo nome logo",
  "settingsDropdown.color": "Cambia colore",
  "settingsDropdown.chooseColor": "Scegli colore",
  "settingsDropdown.switchToAdmin": "Passa ad admin",
  "settingsDropdown.language": "Lingua",
  "settingsDropdown.memberAssistant": "Assistente della conoscenza",
  "memberSidebar.pageTitle": "{appName} Assistente della conoscenza",
  "memberSidebar.newChat": "Avvia nuova chat",
  "memberSidebar.assistant": "Assistente",
  "memberSidebar.noThreads": "Nessuna conversazione",
  "memberSidebar.loadMore": "Carica altro...",
  "memberSidebar.threadActions": "Azioni conversazione",
  "memberSidebar.today": "OGGI",
  "memberSidebar.yesterday": "IERI",
  "memberSidebar.previous7": "ULTIMI 7 GIORNI",
  "memberSidebar.previous30": "ULTIMI 30 GIORNI",
  "memberSidebar.olderThan30": "OLTRE 30 GIORNI",
  "memberSidebar.archiveTitle": "Archivia conversazione",
  "memberSidebar.archiveConfirm": "Vuoi davvero archiviare questa conversazione? Sparira dalla tua vista ma restera visibile agli amministratori.",
  "memberSidebar.archive": "Archivia",
  "assistant.errorGeneric": "Si e verificato un errore. Riprova piu tardi.",
  "assistant.feedbackTooltip": "Il tuo feedback viene controllato dal team. Le valutazioni ci aiutano a individuare risposte errate e a migliorare i documenti di base.",
  "assistant.copySuccess": "Copiato negli appunti",
  "assistant.copyTooltip": "Copia la risposta senza la sezione delle fonti.",
  "assistant.commentAdd": "Aggiungi un commento per il team, ad esempio cosa e sbagliato o manca.",
  "assistant.commentEdit": "Modifica il commento per il team, ad esempio cosa e sbagliato o manca.",
  "assistant.regenerate": "Genera una nuova risposta alla stessa domanda.",
  "assistant.yourComment": "Il tuo commento:",
  "assistant.deleteComment": "Elimina commento",
  "assistant.webPlaceholder": "Cerca sul web... (premi Invio per inviare)",
  "assistant.askPlaceholder": "Fai una domanda... (premi Invio per inviare)",
  "assistant.switchToWeb": "Passa alla ricerca web",
  "assistant.switchToDocs": "Passa alla ricerca nei documenti",
  "assistant.stopRecording": "Interrompi registrazione",
  "assistant.transcribing": "Trascrizione in corso...",
  "assistant.micUnsupported": "La registrazione vocale non e supportata in questo browser",
  "assistant.startRecording": "Avvia registrazione vocale",
  "assistant.askButton": "Chiedi",
  "assistant.transcriptionFailed": "Trascrizione non riuscita",
  "assistant.transcriptionNoText": "Il riconoscimento vocale non ha rilevato alcun testo.",
  "assistant.transcriptionError": "Si e verificato un errore durante il riconoscimento vocale.",
  "assistant.transcriptionTooShort": "La registrazione era troppo breve o vuota. Riprova.",
  "assistant.transcriptionNetwork": "Errore di rete durante il riconoscimento vocale. Controlla la tua connessione internet.",
  "assistant.commentSaved": "Commento salvato",
  "assistant.commentSaveFailed": "Impossibile salvare il commento",
  "assistant.commentDeleted": "Commento eliminato",
  "assistant.commentDeleteFailed": "Impossibile eliminare il commento",
  "assistant.microphonePermission": "Consenti l'accesso al microfono per inviare messaggi vocali.",
  "assistant.microphoneDenied": "Accesso al microfono negato. Consentilo nelle impostazioni del browser.",
  "assistant.microphoneNotFound": "Nessun microfono trovato. Assicurati che un microfono sia collegato.",
  "assistant.microphoneAccessFailed": "Accesso al microfono non riuscito",
  "settings.removePhoto": "Rimuovi foto",
  "documents.folderStructure": "Struttura cartelle",
  "documents.currentFolder": "Corrente",
  "documents.newFolder": "Nuova cartella",
  "documents.deleteFolder": "Elimina cartella",
  "documents.uploadedDocuments": "Documenti caricati",
  "documents.searchPlaceholder": "Cerca documenti...",
  "documents.upload": "Carica",
  "documents.uploadNew": "Nuovo caricamento",
  "documents.folder": "Cartella",
  "documents.filename": "Nome file",
  "documents.uploadedBy": "Caricato da",
  "documents.datetime": "Data/Ora",
  "documents.categories": "Categorie",
  "documents.status": "Stato",
  "documents.actions": "Azioni",
  "documents.subfolder": "Sottocartella",
  "documents.open": "Apri",
  "documents.loading": "Caricamento documenti...",
  "documents.empty": "Nessun documento trovato",
  "documents.pageOf": "Pagina {current} di {total}",
  "documents.pending": "In attesa",
  "documents.processing": "In elaborazione",
  "documents.failed": "Errore",
  "documents.permanentFailed": "Errore permanente",
  "documents.processed": "Elaborato",
  "documents.createFolder": "Crea cartella",
  "documents.folderName": "Nome cartella",
  "documents.parentFolder": "Cartella padre",
  "documents.topLevel": "Livello principale",
  "documents.create": "Crea",
  "documents.selectFiles": "Seleziona file",
  "documents.removeAll": "Rimuovi tutto",
  "documents.browseFiles": "Sfoglia file",
  "documents.categoriesRequired": "Categorie (obbligatorie)",
  "documents.categoriesOptional": "Categorie (facoltative)",
  "documents.supportedFormats": "Formati supportati: PDF, DOCX, XLSX. Max 25 MB per file, 100 MB totali. L'elaborazione puo richiedere diversi minuti.",
  "documents.title": "Documenti",
  "responses.editing": "Modifica",
  "responses.userFeedback": "Feedback utente",
  "responses.adminFeedback": "Feedback admin",
  "responses.comments": "Commenti",
  "responses.withComments": "Con commenti",
  "responses.withoutComments": "Senza commenti",
  "responses.archiving": "Archiviazione",
  "responses.archived": "Archiviato",
  "responses.notArchived": "Non archiviato",
  "responses.allUsers": "Tutti gli utenti",
  "responses.loadingUsers": "Caricamento utenti...",
  "responses.unknownUser": "Utente sconosciuto",
  "responses.unknownContact": "Contatto sconosciuto",
  "responses.allQrCodes": "Tutti i codici QR",
  "responses.loadingQrCodes": "Caricamento codici QR...",
  "responses.newestFirst": "Piu recenti prima",
  "responses.oldestFirst": "Piu vecchi prima",
  "responses.refreshing": "Aggiornamento...",
  "responses.refresh": "Aggiorna",
  "responses.empty": "Nessun messaggio corrisponde ai filtri correnti.",
  "responses.versions": "Versioni",
  "responses.edit": "Modifica",
  "responses.originalAnswer": "Risposta originale",
  "responses.editedAnswer": "Risposta modificata",
  "responses.userComment": "Commento utente:",
  "responses.good": "Buono",
  "responses.bad": "Scarso",
  "responses.correctionTitle": "Inserisci correzione",
  "responses.correctionDescription": "Inserisci la risposta corretta in modo che l'IA possa imparare.",
  "responses.currentAnswer": "Risposta attuale:",
  "responses.correctedAnswer": "Risposta corretta:",
  "settings.title": "Impostazioni",
  "users.title": "Utenti",
};

const dateLocales: Record<Language, Locale> = {
  de,
  en: enUS,
  fr,
  it,
};

const localeCodes: Record<Language, string> = {
  de: "de-CH",
  en: "en-US",
  fr: "fr-FR",
  it: "it-IT",
};

const I18nContext = createContext<I18nContextValue | null>(null);

const interpolate = (template: string, params?: TranslationParams) => {
  if (!params) return template;
  return template.replace(/\{(\w+)\}/g, (_, key: string) =>
    String(params[key] ?? `{${key}}`),
  );
};

const getInitialLanguage = (): Language => {
  if (typeof window === "undefined") return "de";
  const stored = window.localStorage.getItem(STORAGE_KEY) as Language | null;
  if (stored && stored in translations) return stored;
  return "de";
};

export const I18nProvider = ({ children }: { children: ReactNode }) => {
  const [language, setLanguageState] = useState<Language>(getInitialLanguage);

  useEffect(() => {
    window.localStorage.setItem(STORAGE_KEY, language);
    document.documentElement.lang = language;
  }, [language]);

  const value = useMemo<I18nContextValue>(
    () => ({
      language,
      setLanguage: setLanguageState,
      t: (key, params) => {
        const template =
          translations[language][key] ?? translations.en[key] ?? key;
        return interpolate(template, params);
      },
      locale: localeCodes[language],
      dateLocale: dateLocales[language],
    }),
    [language],
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
};

export const useI18n = () => {
  const context = useContext(I18nContext);
  if (!context) {
    throw new Error("useI18n must be used within I18nProvider");
  }
  return context;
};
